import { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import MyButton from "../../../components/MyButton";
import MyPagination from "../../../components/MyPagination";
import { useBoardItem } from "../../../hooks/useBoardItem";
import MyLoading from "../../../components/MyLoading";
import {
  BOARD_PERMISSION,
  NON_MEMBER_PERMISSIONS,
} from "../../../constants/permission";
import { AuthContext } from "../../../context/AuthProvider";
import {
  CheckRoleAndPermissionDisplay,
  checkRoleAndPermissions,
  getPermissionMessage,
} from "../../../components/CheckRoleAndPermission";

const Family0234 = ({ boardCode, parent }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { useGetBoardItems, useGetBoard } = useBoardItem();
  const { roleCode, isAdmin } = useContext(AuthContext);

  const nonPermissions = NON_MEMBER_PERMISSIONS[boardCode];

  const [page, setPage] = useState(1);
  const [maxPage, setMaxPage] = useState(1);
  const [content, setContent] = useState([]);
  const [total, setTotal] = useState(0);

  const { data: board, isLoading: isBoardLoading } = useGetBoard(
    { boardCode },
    (data) => data.data
  );

  const { data, isLoading } = useGetBoardItems({
    size: board?.pageSize,
    page,
    sort: "createdDate,Desc",
    boardCode: board?.boardCode,
    enabled: checkRoleAndPermissions(
      roleCode,
      board,
      BOARD_PERMISSION.ACCESS,
      nonPermissions,
      isAdmin
    ),
  });

  const paginationProps = useMemo(
    () => ({
      size: board?.pageSize,
      page,
      setPage,
      maxPage,
      total,
    }),
    [board?.pageSize, maxPage, page, total]
  );

  const mapData = (listData) => {
    setMaxPage(listData.totalPages);
    setTotal(listData.totalElements);
    setContent(listData.content);
  };

  const checkPermission = (e) => {
    if (
      !checkRoleAndPermissions(
        roleCode,
        board,
        BOARD_PERMISSION.READ,
        nonPermissions,
        isAdmin
      )
    ) {
      alert(getPermissionMessage(BOARD_PERMISSION.READ));
      e.preventDefault();
    }
  };

  useEffect(() => {
    if (!isLoading && data?.data) {
      mapData(data.data);
    }
  }, [isLoading, data]);

  useEffect(() => {
    if (
      !isBoardLoading &&
      !!board &&
      !checkRoleAndPermissions(
        roleCode,
        board,
        BOARD_PERMISSION.ACCESS,
        nonPermissions,
        isAdmin
      )
    )
      alert(getPermissionMessage(BOARD_PERMISSION.ACCESS));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board, isBoardLoading, roleCode, isAdmin]);

  return (
    <CheckRoleAndPermissionDisplay
      showMessage={!isBoardLoading && !!board}
      board={board}
      permission={BOARD_PERMISSION.ACCESS}
      boardCode={board?.boardCode}
    >
      {checkRoleAndPermissions(
        roleCode,
        board,
        BOARD_PERMISSION.WRITE,
        nonPermissions,
        isAdmin
      ) && (
        <div className="flex justify-end">
          <MyButton
            color="primary"
            onClick={() => navigate(`/Family/${parent}/Add`)}
          >
            글 작성하기
          </MyButton>
        </div>
      )}

      {isLoading ? (
        <MyLoading className="h-[150px]" />
      ) : (
        <>
          {content?.length ? (
            <>
              <div className="flex flex-wrap gap-x-[2%] gap-y-6 my-5">
                {content.map((item) => (
                  <div
                    key={item.itemNo}
                    className="xl:w-[32%] sm:w-[49%] w-full"
                  >
                    <Link
                      onClick={checkPermission}
                      className="border border-gray-border rounded-lg flex items-center justify-center overflow-hidden"
                      style={{
                        aspectRatio: 16 / 9,
                      }}
                      to={`/Family/${parent}/${item.itemNo}`}
                    >
                      <img
                        src={
                          item?.thumbnailFiles?.length
                            ? `/api/file/${item.thumbnailFiles[0]?.fileAccessToken}`
                            : "/assets/img/thumb.png"
                        }
                        alt=""
                        className="object-cover w-full h-full rounded-lg"
                      />
                    </Link>
                    <div className="xl:mt-2 my-1">
                      <Link
                        onClick={checkPermission}
                        className="overflow-hidden text-ellipsis line-clamp-1 font-medium"
                        style={{
                          display: "-webkit-box",
                          WebkitBoxOrient: "vertical",
                          wordBreak: "break-all",
                        }}
                        to={`/Family/${parent}/${item.itemNo}`}
                      >
                        {item.title}
                      </Link>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-dark">
                          {item.writer.name}
                        </span>
                        <div className="w-[1px] h-2 bg-gray-dark" />
                        <span className="text-sm text-gray-dark">
                          {dayjs(item.createdDate).format("YYYY-MM-DD")}
                        </span>
                        <div className="w-[1px] h-2 bg-gray-dark" />
                        <span className="text-sm text-gray-dark">
                          조회수 {item.readCount}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-gray-dark text-center py-10">
              {t("common.text.noData")}
            </p>
          )}
        </>
      )}

      {maxPage > 1 && (
        <div className="flex justify-center">
          <MyPagination paginationProps={paginationProps} />
        </div>
      )}
    </CheckRoleAndPermissionDisplay>
  );
};

export default Family0234;
